import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Collapse, Typography } from '@mui/material';
import { ChevronDown, History, RefreshCw } from 'lucide-react';
import kgDatasetService from '../../../services/kgDatasetService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import KgDryRunReport from '../KgDryRunReport';
import styles from './JobsTab.module.scss';
import { describeError, formatDateTime, formatNumber, unwrapList } from '../kgUtils';

/**
 * Ingest history (screen 8).
 *
 * An AUDIT TRAIL, not a progress feed — and the distinction matters for how this
 * is built. Each row is created and completed inside the same request that
 * returns the 202, so no other status is ever observable; and a push that fails
 * outright rolls its row back, so it never appears at all. Hence: no polling
 * here, ever. Live ingestion progress lives on the dataset header instead.
 *
 * What this screen is actually for: "what was pushed, when, and what did the
 * mapper make of it" — the report is the answer to "why does this dataset look
 * wrong", and it is where a failed count links to.
 */
const JobsTab = ({ dataset }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await kgDatasetService.listJobs(dataset.id, { limit: 20 });
      setJobs(unwrapList(data, 'jobs'));
      setError(null);
    } catch (e) {
      setError(describeError(e, "Couldn't load the ingest history."));
    } finally {
      setLoading(false);
    }
  }, [dataset.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <Typography className={sharedStyles.muted}>Loading history…</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (jobs.length === 0) {
    return (
      <div className={sharedStyles.emptyState}>
        <History size={40} className={sharedStyles.emptyIcon} />
        <Typography variant="h6">Nothing pushed yet</Typography>
        <Typography variant="body2" className={sharedStyles.muted}>
          Every push shows up here with what the mapper made of it — which records went in,
          which were already up to date, and why any were rejected.
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <span className={sharedStyles.muted}>Most recent {jobs.length} pushes</span>
        <Button size="small" variant="text" startIcon={<RefreshCw size={14} />} onClick={load}>
          Refresh
        </Button>
      </div>

      <div className={styles.list}>
        {jobs.map((job) => {
          const counts = job.counts || {};
          const isOpen = expanded === job.id;
          return (
            <div key={job.id} className={styles.job}>
              <button
                type="button"
                className={styles.jobHead}
                onClick={() => setExpanded(isOpen ? null : job.id)}
              >
                <ChevronDown
                  size={15}
                  className={styles.chevron}
                  style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                />
                <span className={styles.jobTime}>{formatDateTime(job.created_at)}</span>
                <span className={styles.jobCounts}>
                  <span className={styles.chipOk}>
                    <strong>{formatNumber(counts.ok)}</strong> written
                  </span>
                  {/* `skipped` means already up to date — a deliberate no-op that
                      is what makes a nightly full re-push affordable. Never red. */}
                  {Number(counts.skipped) > 0 && (
                    <span className={styles.chipNeutral}>
                      <strong>{formatNumber(counts.skipped)}</strong> already up to date
                    </span>
                  )}
                  {Number(counts.failed) > 0 && (
                    <span className={styles.chipBad}>
                      <strong>{formatNumber(counts.failed)}</strong> rejected
                    </span>
                  )}
                </span>
                <span className={styles.jobTotal}>
                  {formatNumber(counts.total)} received
                </span>
              </button>

              <Collapse in={isOpen} unmountOnExit>
                <div className={styles.jobBody}>
                  {job.report ? (
                    <KgDryRunReport
                      result={{ report: job.report }}
                      totalFacets={dataset.mapping_config?.facets?.length || 0}
                      compact
                    />
                  ) : (
                    <Typography variant="body2" className={sharedStyles.muted}>
                      No report was recorded for this push.
                    </Typography>
                  )}
                </div>
              </Collapse>
            </div>
          );
        })}
      </div>

      <Typography variant="caption" className={styles.footnote}>
        There is no “retry failed records” — the fix for a rejected record is to correct it at
        the source and push it again. Because the upsert overwrites by key, re-pushing the whole
        file is a safe way to do that.
      </Typography>
    </div>
  );
};

export default JobsTab;
